import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { API } from '@core/services/api.base';
import {
  FeedbackEntity,
  FeedbackStatsEntity,
  CreateFeedbackDto,
} from 'src/app/models/feedback.model';

@Injectable({
  providedIn: 'root',
})
export class FeedbackService extends API {
  /**
   * Get feedback by appointment ID
   * @param appointmentId - The appointment ID to check for feedback
   * @returns Observable<FeedbackEntity | null> - Returns feedback if exists, null otherwise
   */
  getFeedbackByAppointment(
    appointmentId: string
  ): Observable<FeedbackEntity | null> {
    return this.get<FeedbackEntity | null>(
      `v1/feedback/appointment/${appointmentId}`
    ).pipe(
      // If request fails (404, CORS, etc.), assume no feedback exists
      catchError((error) => {
        console.warn(
          `[FEEDBACK_SERVICE] Could not fetch feedback for appointment ${appointmentId}:`,
          error
        );
        return of(null);
      })
    );
  }

  /**
   * Check if feedback exists for an appointment
   * @param appointmentId - The appointment ID to check
   * @returns Observable<boolean> - Returns true if feedback exists, false otherwise
   */
  checkFeedbackExists(appointmentId: string): Observable<boolean> {
    return this.getFeedbackByAppointment(appointmentId).pipe(
      map((feedback) => !!feedback)
    );
  }

  /**
   * Create feedback for an appointment
   * @param createFeedbackDto - The feedback data to create
   * @returns Observable<FeedbackEntity> - Returns created feedback
   */
  createFeedback(
    createFeedbackDto: CreateFeedbackDto
  ): Observable<FeedbackEntity> {
    return this.post<FeedbackEntity, CreateFeedbackDto>(
      'v1/feedback',
      createFeedbackDto
    );
  }

  /**
   * Update existing feedback
   * @param feedbackId - The feedback ID to update
   * @param updateData - The feedback data to update
   * @returns Observable<FeedbackEntity> - Returns updated feedback
   */
  updateFeedback(
    feedbackId: string,
    updateData: Partial<FeedbackEntity>
  ): Observable<FeedbackEntity> {
    return this.put<FeedbackEntity, Partial<FeedbackEntity>>(
      `v1/feedback/${feedbackId}`,
      updateData
    );
  }

  /**
   * Delete feedback
   * @param feedbackId - The feedback ID to delete
   * @returns Observable<void> - Returns void when successfully deleted
   */
  deleteFeedback(feedbackId: string): Observable<void> {
    return this.delete<void>(`v1/feedback/${feedbackId}`);
  }

  /**
   * Get all feedback for a user
   * @param userId - The user ID
   * @returns Observable<FeedbackEntity[]> - Returns array of user's feedback
   */
  getUserFeedback(userId: string): Observable<FeedbackEntity[]> {
    return this.get<FeedbackEntity[]>(`v1/feedback/user/${userId}`).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * Get all feedback for a store
   * @param storeId - The store ID
   * @param limit - Optional limit for number of feedback items
   * @returns Observable<FeedbackEntity[]> - Returns array of store's feedback
   */
  getStoreFeedback(
    storeId: string,
    limit?: number
  ): Observable<FeedbackEntity[]> {
    const url = limit
      ? `v1/feedback/store/${storeId}?limit=${limit}`
      : `v1/feedback/store/${storeId}`;
    return this.get<FeedbackEntity[]>(url).pipe(catchError(() => of([])));
  }

  /**
   * Get feedback statistics for a store
   * @param storeId - The store ID
   * @returns Observable<FeedbackStatsEntity> - Returns store feedback statistics
   */
  getStoreFeedbackStats(storeId: string): Observable<FeedbackStatsEntity> {
    return this.get<FeedbackStatsEntity>(`v1/feedback/stats/${storeId}`).pipe(
      catchError(() =>
        of({
          storeId,
          totalHeartsLiked: 0,
          lastUpdated: new Date(),
        })
      )
    );
  }

  /**
   * Get all feedback statistics across all stores
   * @returns Observable<FeedbackStatsEntity[]> - Returns array of feedback statistics for all stores
   */
  getAllFeedbackStats(): Observable<FeedbackStatsEntity[]> {
    return this.get<FeedbackStatsEntity[]>('v1/feedback/stats').pipe(
      catchError(() => of([]))
    );
  }
}
